// Gestor de <head> por página, sin dependencias externas.
// Hace "upsert" de title/description/canonical/OG/Twitter sobre los tags que
// ya existen en index.html (una sola fuente de verdad, sin duplicados) e
// inyecta los bloques JSON-LD marcados con data-seo-jsonld.
//
// Importante para el prerender: estos efectos corren en el Chrome headless
// ANTES de que tomemos el snapshot, así que el HTML resultante incluye el head
// ya poblado. En el cliente, al navegar entre rutas, se actualiza/limpia.

import { useEffect } from 'react';
import { SITE_URL } from '../lib/structuredData';

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const removeMeta = (attr, key) => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
};

// Igual que upsertMeta, pero `null` explícito BORRA el tag (undefined lo deja
// como esté). index.html declara unas dimensiones y un tipo de og:image propios
// de la portada; una página con su propia imagen necesita poder quitarlos, o
// anuncia 1200x630 jpeg sobre una foto que no lo es.
const upsertOrRemoveMeta = (attr, key, content) => {
  if (content === undefined) return;
  if (content === null) return removeMeta(attr, key);
  upsertMeta(attr, key, content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo = ({
  title,
  description,
  path = '/',
  image = `${SITE_URL}/og-image.jpg`,
  imageWidth,   // opcional: si la página usa una OG con dimensiones propias
  imageHeight,  // (p.ej. /udla usa una cuadrada 1200x1200), sobreescribe los
  imageAlt,     // defaults de index.html. Sin estas props, se dejan intactos;
  imageType,    // con null explícito, se BORRAN (imágenes de tamaño variable).
  type = 'website',
  noindex = false,
  jsonLd = [],
}) => {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;

    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', canonical);
    // index.html trae "index, follow"; una página que no debe indexarse (p.ej.
    // un producto que ya no existe) lo sobreescribe, y al navegar a otra ruta
    // este mismo efecto lo restaura.
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    // Open Graph
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', 'Brazolovers');
    upsertMeta('property', 'og:locale', 'es_EC');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    // Solo se sobreescriben cuando la página las provee (null → upsert los ignora).
    upsertOrRemoveMeta('property', 'og:image:width', imageWidth === null ? null : (imageWidth != null ? String(imageWidth) : undefined));
    upsertOrRemoveMeta('property', 'og:image:height', imageHeight === null ? null : (imageHeight != null ? String(imageHeight) : undefined));
    upsertOrRemoveMeta('property', 'og:image:type', imageType);
    upsertMeta('property', 'og:image:alt', imageAlt);

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    // JSON-LD: limpiar los inyectados antes y volver a poner los de esta página.
    document
      .querySelectorAll('script[data-seo-jsonld]')
      .forEach((n) => n.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.filter(Boolean).forEach((block) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '');
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
  }, [title, description, path, image, imageWidth, imageHeight, imageAlt, imageType, type, noindex, jsonLd]);

  return null;
};

export default Seo;
